import { api, fmtDate, fmtDuration, fmtEuro, fmtHours } from "../api";
import { add, clear, confirmDialog, h, modal, toast } from "../dom";
import type { Project, User } from "../types";
import { fmtRate, renderStatement } from "./statement";
import type { LedgerEntry, Statement, WorkEntry } from "./statement";

type Tab = "users" | "projects" | "ingest" | "tiles" | "export" | "time" | "pay";

export function renderAdmin(root: HTMLElement, user: User): void {
  const content = h("div", { className: "page" });
  let tab: Tab = (location.hash.split("/")[2] as Tab) || "users";
  const tabs: [Tab, string][] = [
    ["users", "Users"], ["projects", "Projects"], ["ingest", "Ingest"], ["tiles", "Tiles"],
    ["export", "Export"], ["time", "Time"], ["pay", "Pay"],
  ];
  const tabbar = h("div", { className: "tabbar" });
  let reviewCount = 0;
  void api<{ count: number }>("/api/admin/review-queue").then((r) => { reviewCount = r.count; drawTabs(); }).catch(() => {});

  function drawTabs(): void {
    clear(tabbar);
    for (const [t, label] of tabs) {
      tabbar.append(
        h("button", {
          className: "tab" + (tab === t ? " selected" : ""),
          onClick: () => { tab = t; location.hash = `#/admin/${t}`; drawTabs(); void drawTab(); },
        }, label, t === "tiles" && reviewCount ? h("span", { className: "tab-badge", title: `${reviewCount} tile(s) waiting for review` }, String(reviewCount)) : null),
      );
    }
  }

  async function drawTab(): Promise<void> {
    clear(content);
    content.append(h("p", { className: "muted pad" }, "Loading…"));
    try {
      if (tab === "users") await usersTab(content);
      else if (tab === "projects") await projectsTab(content);
      else if (tab === "ingest") await ingestTab(content);
      else if (tab === "tiles") await tilesTab(content);
      else if (tab === "export") await exportTab(content);
      else if (tab === "pay") await payTab(content);
      else await timeTab(content);
    } catch (e: any) {
      clear(content);
      content.append(h("p", { className: "form-error pad" }, e.message));
    }
  }

  root.append(
    h(
      "div",
      { className: "topbar" },
      h("span", { className: "brand" }, "Admin"),
      tabbar,
      h("span", { className: "spacer" }),
      h("button", { className: "btn btn-small", onClick: () => (location.hash = "#/") }, "Back to labeling"),
    ),
    content,
  );
  drawTabs();
  void drawTab();
}

// ---------------- helpers ----------------

async function pickProject(content: HTMLElement, onPick: (p: Project) => void): Promise<Project[] | null> {
  const projects = await api<Project[]>("/api/projects");
  clear(content);
  if (projects.length === 0) {
    content.append(h("p", { className: "muted pad" }, "No projects yet."));
    return null;
  }
  const saved = Number(localStorage.getItem("labeling.adminProject"));
  let current = projects.find((p) => p.id === saved) || projects[0];
  const select = h(
    "select",
    { onChange: () => {
        current = projects.find((p) => p.id === Number(select.value))!;
        localStorage.setItem("labeling.adminProject", select.value);
        onPick(current);
      } },
    ...projects.map((p) => h("option", { value: p.id, selected: p.id === current.id }, p.name)),
  );
  content.append(h("div", { className: "row gap pad" }, h("label", {}, "Project "), select));
  onPick(current);
  return projects;
}

function passwordModal(username: string, password: string): void {
  modal(
    h("div", {},
      h("h2", {}, `Password for ${username}`),
      h("p", {}, "Copy it now — it is shown only once."),
      h("div", { className: "pw-box" }, h("code", {}, password),
        h("button", { className: "btn btn-small", onClick: () => { void navigator.clipboard.writeText(password); toast("Copied"); } }, "Copy")),
    ),
  );
}

// ---------------- users ----------------

async function usersTab(content: HTMLElement): Promise<void> {
  const users = await api<any[]>("/api/admin/users");
  clear(content);

  const usernameInput = h("input", { placeholder: "new username" });
  const adminCheck = h("input", { type: "checkbox" });
  content.append(
    h("form", { className: "row gap pad", onSubmit: async (e: Event) => {
        e.preventDefault();
        try {
          const r = await api<any>("/api/admin/users", { method: "POST", body: { username: usernameInput.value, is_admin: adminCheck.checked } });
          passwordModal(r.username, r.password);
          void usersTab(content);
        } catch (err: any) { toast(err.message, true); }
      } },
      usernameInput,
      h("label", { className: "row" }, adminCheck, " admin"),
      h("button", { className: "btn btn-primary btn-small", type: "submit" }, "Add user"),
      h("span", { className: "spacer" }),
      h("button", { className: "btn btn-small", type: "button", onClick: async () => {
          if (await confirmDialog("Pause ALL users? They can finish claimed tiles but not claim new ones.", "Pause all")) {
            await api("/api/admin/users/pause-all", { method: "POST", body: { paused: true } });
            void usersTab(content);
          }
        } }, "Pause all"),
      h("button", { className: "btn btn-small", type: "button", onClick: async () => {
          await api("/api/admin/users/pause-all", { method: "POST", body: { paused: false } });
          void usersTab(content);
        } }, "Resume all"),
    ),
    h("table", { className: "table" },
      h("thead", {}, h("tr", {},
        ...["User", "Hash", "Role", "State", "Time", "Done", "Working", "Review", ""].map((t) => h("th", {}, t)))),
      h("tbody", {}, ...users.map((u) =>
        h("tr", { className: u.is_active ? "" : "row-inactive" },
          h("td", {}, u.username),
          h("td", {}, h("code", {}, u.user_hash)),
          h("td", {}, u.is_admin ? "admin" : "labeler"),
          h("td", {}, !u.is_active ? "deactivated" : u.is_paused ? "paused" : "active"),
          h("td", {}, fmtDuration(u.total_seconds)),
          h("td", {}, String(u.tiles_done ?? 0)),
          h("td", {}, String(u.tiles_active ?? 0) + (u.tiles_review ? ` (+${u.tiles_review} in review)` : "")),
          h("td", {}, reviewCell(u, () => void usersTab(content))),
          h("td", { className: "row gap" },
            !!u.is_active && h("button", { className: "btn btn-small", onClick: async () => {
                const r = await api<any>(`/api/admin/users/${u.id}/reset-password`, { method: "POST" });
                passwordModal(u.username, r.password);
              } }, "Reset PW"),
            !!u.is_active && h("button", { className: "btn btn-small", onClick: async () => {
                await api(`/api/admin/users/${u.id}/pause`, { method: "POST", body: { paused: !u.is_paused } });
                void usersTab(content);
              } }, u.is_paused ? "Resume" : "Pause"),
            !!u.is_active && h("button", { className: "btn btn-small", onClick: async () => {
                const q = u.is_admin
                  ? `Revoke admin rights from ${u.username}?`
                  : `Make ${u.username} an admin? They get full access: users, projects, ingestion, exports and tile management.`;
                if (await confirmDialog(q, u.is_admin ? "Revoke admin" : "Make admin")) {
                  try {
                    await api(`/api/admin/users/${u.id}/set-admin`, { method: "POST", body: { is_admin: !u.is_admin } });
                    void usersTab(content);
                  } catch (e: any) { toast(e.message, true); }
                }
              } }, u.is_admin ? "Revoke admin" : "Make admin"),
            !!u.is_active && h("button", { className: "btn btn-small btn-danger", onClick: () => deactivateDialog(u, () => void usersTab(content)) }, "Delete"),
            !u.is_active && h("button", { className: "btn btn-small", onClick: async () => {
                if (await confirmDialog(`Reactivate ${u.username}? Their password and finished labels are kept; they can log in again.`, "Reactivate")) {
                  await api(`/api/admin/users/${u.id}/reactivate`, { method: "POST" });
                  toast(`${u.username} reactivated`);
                  void usersTab(content);
                }
              } }, "Reactivate"),
          ),
        ))),
    ),
  );
}

const fmtK = (px: number) => px >= 1000 ? `${(px / 1000).toFixed(1)}k` : String(Math.round(px));

/** Users tab: needs-review toggle, claim cap, and the quality figures that
    tell an admin when to switch the review off (docs/review.md). */
function reviewCell(u: any, refresh: () => void): HTMLElement {
  if (u.is_admin || !u.is_active) return h("span", { className: "muted" }, u.is_admin ? "exempt" : "–");
  const save = async (needs_review: boolean, claim_cap: number | null) => {
    try {
      await api(`/api/admin/users/${u.id}/review-settings`, { method: "POST", body: { needs_review, claim_cap } });
      refresh();
    } catch (e: any) { toast(e.message, true); }
  };
  const check = h("input", { type: "checkbox", checked: !!u.needs_review, title: "Tiles go through an admin review before they count as done",
    onChange: () => void save(check.checked, u.claim_cap ?? null) });
  const cap = h("input", { type: "number", min: 1, max: 50, value: u.claim_cap ?? "", placeholder: "cap", style: { width: "58px" },
    title: "Parallel-claim limit for this user (active + in-review tiles while reviewed); empty = project default",
    onChange: () => void save(check.checked, cap.value ? Number(cap.value) : null) });
  const stats = u.review_submitted
    ? `${u.review_submitted} submitted · ${u.review_first_round ?? 0} accepted first time` + (u.review_avg_rounds ? ` · ø ${u.review_avg_rounds} rounds` : "")
    : "no reviews yet";
  return h("div", { className: "review-cell" },
    h("label", { className: "row gap" }, check, " review", cap),
    h("span", { className: "muted small" }, stats));
}

function deactivateDialog(u: any, onDone: () => void): void {
  const input = h("input", { placeholder: u.username });
  const error = h("div", { className: "form-error" });
  const close = modal(
    h("form", { onSubmit: async (e: Event) => {
        e.preventDefault();
        try {
          await api(`/api/admin/users/${u.id}/deactivate`, { method: "POST", body: { confirm_username: input.value } });
          close();
          toast(`${u.username} deactivated`);
          onDone();
        } catch (err: any) { error.textContent = err.message; }
      } },
      h("h2", {}, `Delete user ${u.username}?`),
      h("p", {}, "The account is deactivated and their finished labels are kept. Unfinished tiles go back to the queue. Type the username to confirm:"),
      input, error,
      h("div", { className: "row gap right" }, h("button", { className: "btn btn-danger", type: "submit" }, "Deactivate")),
    ),
  );
}

// ---------------- projects ----------------

async function projectsTab(content: HTMLElement): Promise<void> {
  const body = h("div", {});
  const projects = await api<Project[]>("/api/projects");
  clear(content);

  const nameInput = h("input", { placeholder: "new project name" });
  content.append(
    h("form", { className: "row gap pad", onSubmit: async (e: Event) => {
        e.preventDefault();
        try {
          await api("/api/admin/projects", { method: "POST", body: { name: nameInput.value } });
          void projectsTab(content);
        } catch (err: any) { toast(err.message, true); }
      } },
      nameInput, h("button", { className: "btn btn-primary btn-small", type: "submit" }, "Create project")),
    body,
  );
  if (projects.length === 0) return;

  const saved = Number(localStorage.getItem("labeling.adminProject"));
  let project = projects.find((p) => p.id === saved) || projects[0];
  const select = h("select", { onChange: () => {
      project = projects.find((p) => p.id === Number(select.value))!;
      localStorage.setItem("labeling.adminProject", select.value);
      drawProject();
    } },
    ...projects.map((p) => h("option", { value: p.id, selected: p.id === project.id }, p.name)));
  const detail = h("div", {});
  body.append(h("div", { className: "row gap pad" }, h("label", {}, "Project "), select), detail);

  async function patchProject(patch: any): Promise<void> {
    project = await api<Project>(`/api/admin/projects/${project.id}`, { method: "PUT", body: patch });
    const i = projects.findIndex((p) => p.id === project.id);
    projects[i] = project;
    drawProject();
  }

  function drawProject(): void {
    clear(detail);
    const s = project.settings;
    const num = (label: string, key: keyof typeof s, min: number, max: number) => {
      const input = h("input", { type: "number", min, max, value: s[key] as number, style: { width: "5em" },
        onChange: () => void patchProject({ settings: { [key]: Number(input.value) } }) });
      return h("label", { className: "row gap" }, label, input);
    };
    detail.append(
      h("section", { className: "pad" },
        h("h2", {}, "Label settings (defaults for new users)"),
        h("div", { className: "row gap wrap" },
          num("Snap distance", "snap_dist", 0, 60),
          num("Paint node spacing", "node_spacing", 5, 500),
          num("Line width", "line_width", 1, 12),
          num("Max parallel claims per user", "max_parallel_claims", 1, 100),
          num("Inactivity timeout (s, pauses time tracking)", "inactivity_timeout", 10, 3600),
          h("label", { className: "row gap" },
            h("input", { type: "checkbox", checked: s.snap_intersections,
              onChange: (e: Event) => void patchProject({ settings: { snap_intersections: (e.target as HTMLInputElement).checked } }) }),
            "Snap at intersections"),
        ),
      ),
      classesSection(),
      modalitiesSection(),
    );
  }

  function classesSection(): HTMLElement {
    const nameIn = h("input", { placeholder: "class name (e.g. road)" });
    return h("section", { className: "pad" },
      h("h2", {}, "Classes"),
      h("table", { className: "table" },
        h("thead", {}, h("tr", {}, ...["Color", "Name", "Type", "Active", ""].map((t) => h("th", {}, t)))),
        h("tbody", {}, ...project.classes.map((c) => {
          const colorIn = h("input", { type: "color", value: c.color,
            onChange: async () => { await api(`/api/admin/classes/${c.id}`, { method: "PUT", body: { color: colorIn.value } }); } });
          return h("tr", { className: c.is_active ? "" : "row-inactive" },
            h("td", {}, colorIn),
            h("td", {}, c.name),
            h("td", {}, c.geom_type),
            h("td", {}, c.is_active ? "yes" : "no"),
            h("td", {}, h("button", { className: "btn btn-small", onClick: async () => {
                await api(`/api/admin/classes/${c.id}`, { method: "PUT", body: { is_active: !c.is_active } });
                void projectsTab(content);
              } }, c.is_active ? "Deactivate" : "Activate")));
        })),
      ),
      h("form", { className: "row gap", onSubmit: async (e: Event) => {
          e.preventDefault();
          try {
            await api(`/api/admin/projects/${project.id}/classes`, { method: "POST", body: { name: nameIn.value } });
            void projectsTab(content);
          } catch (err: any) { toast(err.message, true); }
        } },
        nameIn, h("button", { className: "btn btn-small btn-primary", type: "submit" }, "Add class (line)")),
    );
  }

  function modalitiesSection(): HTMLElement {
    const nameIn = h("input", { placeholder: "modality name (e.g. RGB)" });
    const suffixIn = h("input", { placeholder: "filename suffix (e.g. rgb, empty = default)" });
    return h("section", { className: "pad" },
      h("h2", {}, "Modalities"),
      h("p", { className: "muted" },
        "Files are matched as <basename>_<suffix>.tif. The reference modality's geotransform is used to georeference labels."),
      h("table", { className: "table" },
        h("thead", {}, h("tr", {}, ...["Name", "Suffix", "Clamp (on ingest)", "Elevation", "Reference", ""].map((t) => h("th", {}, t)))),
        h("tbody", {}, ...project.modalities.map((m) =>
          h("tr", {},
            h("td", {}, m.name),
            h("td", {}, h("code", {}, m.suffix || "(none)")),
            h("td", {}, stretchEditor(m, () => void projectsTab(content))),
            h("td", {}, h("label", { title: "DTM-like elevation raster: ingest also builds a height grid, enabling the 3D view; not offered as a 2D image layer. Re-run ingest (with the input files present) for already-ingested tiles." },
              h("input", { type: "checkbox", checked: !!m.is_elevation, onChange: async (e: Event) => {
                  try {
                    await api(`/api/admin/modalities/${m.id}`, { method: "PUT", body: { is_elevation: (e.target as HTMLInputElement).checked } });
                    toast("Saved — re-run ingest to build height grids for existing tiles");
                  } catch (err: any) { toast(err.message, true); }
                } }))),
            h("td", {}, m.id === project.reference_modality_id
              ? h("strong", {}, "reference")
              : h("button", { className: "btn btn-small", onClick: () => void patchProject({ reference_modality_id: m.id }) }, "Make reference")),
            h("td", {}, h("button", { className: "btn btn-small", onClick: async () => {
                try {
                  await api(`/api/admin/modalities/${m.id}`, { method: "DELETE" });
                  void projectsTab(content);
                } catch (err: any) { toast(err.message, true); }
              } }, "Delete")),
          ))),
      ),
      h("p", { className: "muted" }, "Clamp changes apply to future ingestions only — delete and re-ingest a tile to re-convert it."),
      h("form", { className: "row gap", onSubmit: async (e: Event) => {
          e.preventDefault();
          try {
            await api(`/api/admin/projects/${project.id}/modalities`, { method: "POST", body: { name: nameIn.value, suffix: suffixIn.value } });
            void projectsTab(content);
          } catch (err: any) { toast(err.message, true); }
        } },
        nameIn, suffixIn, h("button", { className: "btn btn-small btn-primary", type: "submit" }, "Add modality")),
    );
  }

  drawProject();
}

function stretchEditor(m: any, onSaved: () => void): HTMLElement {
  const box = h("div", { className: "row gap wrap" });
  const st = m.stretch || {};
  const mode = h("select", {},
    h("option", { value: "minmax", selected: !st.mode || st.mode === "minmax" }, "min/max"),
    h("option", { value: "fixed", selected: st.mode === "fixed" }, "fixed"),
    h("option", { value: "percentile", selected: st.mode === "percentile" }, "percentile"));
  const fields = h("span", { className: "row gap" });
  const numIn = (value: number, placeholder: string) =>
    h("input", { type: "number", step: "any", value, placeholder, style: { width: "5.5em" }, title: placeholder });
  let inputs: HTMLInputElement[] = [];
  const drawFields = () => {
    fields.textContent = "";
    if (mode.value === "fixed") {
      inputs = [numIn(st.min ?? 0, "min"), numIn(st.max ?? 255, "max")];
    } else if (mode.value === "percentile") {
      inputs = [numIn(st.plow ?? 5, "low %"), numIn(st.phigh ?? 95, "high %")];
    } else {
      inputs = [];
    }
    fields.append(...inputs);
  };
  mode.addEventListener("change", drawFields);
  drawFields();
  box.append(mode, fields,
    h("button", { className: "btn btn-small", onClick: async () => {
        const stretch: any = { mode: mode.value };
        if (mode.value === "fixed") { stretch.min = Number(inputs[0].value); stretch.max = Number(inputs[1].value); }
        if (mode.value === "percentile") { stretch.plow = Number(inputs[0].value); stretch.phigh = Number(inputs[1].value); }
        try {
          await api(`/api/admin/modalities/${m.id}`, { method: "PUT", body: { stretch } });
          toast("Clamp saved");
          onSaved();
        } catch (e: any) { toast(e.message, true); }
      } }, "Save"));
  return box;
}

function deleteImagesDialog(project: Project, onDone: () => void): void {
  const input = h("input", { placeholder: project.name });
  const error = h("div", { className: "form-error" });
  const close = modal(
    h("form", { onSubmit: async (e: Event) => {
        e.preventDefault();
        try {
          const r = await api<any>(`/api/admin/projects/${project.id}/delete-images`, {
            method: "POST", body: { confirm_name: input.value },
          });
          close();
          toast(`${r.images_deleted} image record(s) and ${r.files_removed} file(s) deleted — re-ingest now`);
          onDone();
        } catch (err: any) { error.textContent = err.message; }
      } },
      h("h2", {}, `Delete all converted images of ${project.name}?`),
      h("p", {},
        "Removes every converted JPEG and image record of this project so the rasters can be ",
        "re-ingested with the current clamp settings. Tiles, claims, labels and seeds are kept, ",
        "but tiles cannot be opened or claimed until their rasters are re-ingested. ",
        "The original GeoTIFFs must (again) be in the input folder. Type the project name to confirm:"),
      input, error,
      h("div", { className: "row gap right" }, h("button", { className: "btn btn-danger", type: "submit" }, "Delete images")),
    ),
  );
}

/** Case-insensitive glob match supporting * and ? (for template file patterns). */
function globMatch(glob: string, name: string): boolean {
  if (!glob.trim()) return false;
  const rx = glob.toLowerCase().split(/([*?])/).map((part) =>
    part === "*" ? ".*" : part === "?" ? "." : part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
  ).join("");
  return new RegExp(`^${rx}$`).test(name.toLowerCase());
}

// ---------------- ingest ----------------

async function ingestTab(content: HTMLElement): Promise<void> {
  const body = h("div", { className: "pad" });
  const masksBody = h("div", { className: "pad" });
  const labelsBody = h("div", { className: "pad" });
  let project: Project | null = null;
  const projects = await pickProject(content, (p) => {
    project = p;
    clear(body);
    drawIntro();
    clear(masksBody);
    void drawMasks();
    clear(labelsBody);
    drawLabelsIntro();
  });
  if (!projects) return;
  content.append(body, masksBody, labelsBody);

  function drawIntro(): void {
    body.append(
      h("p", { className: "muted" },
        "Drop GeoTIFFs into the input folder on the server (data/input), then scan. ",
        "Scan shows what would happen; Ingest converts to JPEG and stores the georeferencing."),
      h("div", { className: "row gap" },
        h("button", { className: "btn", onClick: () => void run(true) }, "Scan input folder"),
        h("button", { className: "btn btn-primary", onClick: () => void run(false) }, "Ingest now"),
        h("span", { className: "spacer" }),
        h("button", { className: "btn btn-small btn-danger", onClick: () => deleteImagesDialog(project!, () => void run(true)) },
          "Delete all converted images…")),
    );
  }

  async function drawMasks(): Promise<void> {
    const p = project!;
    let masks: any[];
    try {
      masks = await api<any[]>(`/api/admin/projects/${p.id}/mask-files`);
    } catch { return; }
    if (p !== project) return;
    clear(masksBody);
    masksBody.append(
      h("h2", {}, "Ground-truth masks"),
      h("p", { className: "muted" },
        "Each ", h("code", {}, "<prefix>_mask.gpkg"), " blacks out everything outside its polygons when a matching ",
        "raster is ingested (longest matching prefix wins). Untick a mask to ingest its rasters unmasked. ",
        "Changes apply to future ingestions only — to change an already-ingested tile, delete its images and re-ingest."),
    );
    if (masks.length === 0) {
      masksBody.append(h("p", { className: "muted" }, "No *_mask.gpkg files in the input folder."));
      return;
    }
    const tileList = (m: any): HTMLElement | string => {
      const tiles: any[] = m.matched_tiles ?? [];
      if (tiles.length === 0) return "—";
      const names = tiles.slice(0, 3).map((t) => t.basename + (t.masked ? "" : " (unmasked)")).join(", ")
        + (tiles.length > 3 ? ` … +${tiles.length - 3} more` : "");
      return h("span", { title: tiles.map((t) => t.basename + (t.masked ? " (masked)" : " (unmasked)")).join("\n") },
        `${tiles.length}: ${names}`);
    };
    const maskCbs: [any, HTMLInputElement][] = [];
    const allMasksCb = h("input", { type: "checkbox", title: "Enable / disable all masks",
      checked: masks.every((m) => m.enabled || m.error), onChange: async () => {
        for (const [m, cb] of maskCbs) {
          if (cb.disabled || cb.checked === allMasksCb.checked) continue;
          try {
            await api(`/api/admin/projects/${p.id}/mask-files`, { method: "PUT", body: { file: m.file, enabled: allMasksCb.checked } });
            cb.checked = allMasksCb.checked;
          } catch (e: any) { toast(e.message, true); }
        }
        toast(allMasksCb.checked ? "All masks enabled" : "All masks disabled — rasters will be ingested unmasked");
      } });
    masksBody.append(
      h("table", { className: "table" },
        h("thead", {}, h("tr", {}, h("th", {}, allMasksCb, " Apply"),
          ...["Mask file", "Polygons", "Ingested tiles it matched", "Pending input files it would mask"]
            .map((t) => h("th", {}, t)))),
        h("tbody", {}, ...masks.map((m) => {
          const cb = h("input", { type: "checkbox", checked: m.enabled, disabled: !!m.error, onChange: async () => {
            try {
              await api(`/api/admin/projects/${p.id}/mask-files`, { method: "PUT", body: { file: m.file, enabled: cb.checked } });
              allMasksCb.checked = maskCbs.every(([, c]) => c.checked || c.disabled);
              toast(cb.checked
                ? `${m.file} will be applied on future ingestions`
                : `${m.file} disabled — its rasters will be ingested unmasked`);
            } catch (e: any) { cb.checked = !cb.checked; toast(e.message, true); }
          } });
          maskCbs.push([m, cb]);
          const pending: string[] = m.pending_files ?? [];
          return h("tr", { className: m.error ? "row-bad" : "" },
            h("td", {}, cb),
            h("td", {}, m.file),
            h("td", {}, m.error ? m.error : String(m.feature_count ?? "–")),
            h("td", {}, tileList(m)),
            h("td", { title: pending.join("\n") }, pending.length === 0 ? "—"
              : `${pending.length}: ${pending.slice(0, 3).join(", ")}${pending.length > 3 ? ` … +${pending.length - 3} more` : ""}`));
        }))),
    );
  }

  function drawLabelsIntro(): void {
    labelsBody.append(
      h("h2", {}, "Pre-labeled data"),
      h("p", { className: "muted" },
        "Place a ", h("code", {}, "<prefix>_labels.gpkg"), " next to the rasters (same CRS, line layers). ",
        "One file covers every tile whose basename equals the prefix or starts with it (case-insensitive, at a _ / - / . boundary) — ",
        "e.g. BY_Coburg_Labels.gpkg seeds all BY_Coburg_<x>_<y> tiles; geometry is clipped to each tile. ",
        "Scan reads each file's candidate class columns; pick the class column and mapping template per file (defaults are guessed), then import. ",
        "Imported labels become the starting graph for whoever claims the tile."),
      h("div", { className: "row gap" },
        h("button", { className: "btn", onClick: () => void drawLabels() }, "Scan label files")),
    );
  }

  /** Default class column: exact "class" (any case), else something containing
   *  class/klasse, else the first candidate. */
  function defaultClassColumn(cols: string[]): string {
    return cols.find((c) => /^class$/i.test(c))
      ?? cols.find((c) => /class|klasse/i.test(c))
      ?? cols[0] ?? "";
  }

  async function drawLabels(): Promise<void> {
    clear(labelsBody);
    drawLabelsIntro();
    const p = project!;
    const [files, templates] = await Promise.all([
      api<any[]>(`/api/admin/projects/${p.id}/label-files`),
      api<any[]>(`/api/admin/projects/${p.id}/ingest-templates`),
    ]);
    if (files.length === 0) {
      labelsBody.append(h("p", { className: "muted" }, "No *_labels.gpkg files in the input folder."));
      return;
    }
    // per-row selections: checkbox + class column + template
    const rows = new Map<string, { cb: HTMLInputElement; col: HTMLSelectElement; tpl: HTMLSelectElement }>();
    const allFilesCb = h("input", { type: "checkbox", checked: true, title: "Select / deselect all files",
      onChange: () => { for (const r of rows.values()) r.cb.checked = allFilesCb.checked; } });
    labelsBody.append(
      h("table", { className: "table" },
        h("thead", {}, h("tr", {}, h("th", {}, allFilesCb),
          ...["File", "Matched tiles", "Features", "Class column", "Template", "Status"].map((t) => h("th", {}, t)))),
        h("tbody", {}, ...files.map((f) => {
          const matched: any[] = f.matched_tiles ?? [];
          const ok = matched.length > 0 && !f.error;
          const cb = h("input", { type: "checkbox", checked: ok, disabled: !ok });
          const seeded = matched.filter((t) => t.seeded).length;
          const names = matched.slice(0, 3).map((t) => t.basename).join(", ")
            + (matched.length > 3 ? ` … +${matched.length - 3} more` : "");
          const cols = Object.keys(f.inspect?.columns ?? {});
          const colSel = h("select", { disabled: !ok },
            ...cols.map((c) => h("option", { value: c, selected: c === defaultClassColumn(cols) },
              `${c} (${(f.inspect.columns[c] as string[]).length})`)));
          const tplHits = templates.filter((t) => globMatch(t.config?.file_glob ?? "", f.file));
          const preselect = tplHits.length === 1 ? tplHits[0].id : templates.length === 1 ? templates[0].id : null;
          const tplSel = h("select", { disabled: !ok },
            h("option", { value: "" }, "—"),
            ...templates.map((t) => h("option", { value: t.id, selected: t.id === preselect }, t.name)));
          if (ok) rows.set(f.file, { cb, col: colSel, tpl: tplSel });
          return h("tr", { className: f.error || matched.length === 0 ? "row-bad" : "" },
            h("td", {}, cb),
            h("td", {}, f.file),
            h("td", {}, matched.length === 0
              ? `no tiles match prefix "${f.prefix}" (project has ${f.project_tile_count ?? "?"} ingested tiles)`
              : h("span", { title: matched.map((t) => t.basename).join("\n") },
                  `${matched.length}: ${names}`, seeded ? h("span", { className: "muted" }, ` (${seeded} seeded)`) : null)),
            h("td", {}, String(f.inspect?.feature_count ?? "–")),
            h("td", {}, cols.length ? colSel : h("span", { className: "muted" }, "–")),
            h("td", {}, templates.length ? tplSel : h("span", { className: "muted" }, "no templates yet")),
            h("td", {}, f.error ? f.error : "ready"));
        })),
      ),
      importControls(p, rows),
      templatesEditor(p, files, templates),
    );
  }

  function importControls(p: Project, rows: Map<string, { cb: HTMLInputElement; col: HTMLSelectElement; tpl: HTMLSelectElement }>): HTMLElement {
    const report = h("div", {});
    const box = h("div", {},
      h("div", { className: "row gap" },
        h("button", { className: "btn btn-primary btn-small", onClick: async () => {
            const specs = [...rows.entries()]
              .filter(([, r]) => r.cb.checked)
              .map(([file, r]) => ({ file, column: r.col.value, template_id: r.tpl.value ? Number(r.tpl.value) : null }));
            if (!specs.length) { toast("No files selected", true); return; }
            const missing = specs.filter((s) => !s.template_id);
            if (missing.length) { toast(`No template selected for: ${missing.map((s) => s.file).join(", ")}`, true); return; }
            clear(report);
            report.append(h("p", { className: "muted" }, "Importing…"));
            try {
              const r = await api<any>(`/api/admin/projects/${p.id}/import-labels`, {
                method: "POST", body: { files: specs },
              });
              clear(report);
              report.append(h("table", { className: "table" },
                h("thead", {}, h("tr", {}, ...["File", "Tile", "Status", "Details"].map((t) => h("th", {}, t)))),
                h("tbody", {}, ...r.results.map((x: any) =>
                  h("tr", { className: x.status === "error" ? "row-bad" : "" },
                    h("td", {}, x.file), h("td", {}, x.basename ?? "–"), h("td", {}, x.status), h("td", {}, x.message))))));
            } catch (e: any) { clear(report); toast(e.message, true); }
          } }, "Import checked files")),
      report,
    );
    return box;
  }

  function templatesEditor(p: Project, files: any[], templates: any[]): HTMLElement {
    const box = h("div", {});
    const activeClasses = p.classes.filter((c) => c.is_active);
    const inspectable = files.filter((f) => f.inspect);

    // union of values per column across all scanned files
    const columnValues: Record<string, Set<string>> = {};
    for (const f of inspectable) {
      for (const [col, vals] of Object.entries(f.inspect.columns as Record<string, string[]>)) {
        (columnValues[col] ??= new Set());
        for (const v of vals) columnValues[col].add(v);
      }
    }

    const tplSelect = h("select", {},
      ...templates.map((t) => h("option", { value: t.id }, t.name)),
      h("option", { value: "new" }, "— new template —"));
    if (templates.length === 0) tplSelect.value = "new";

    const editor = h("div", {});
    const drawEditor = () => {
      clear(editor);
      const existing = templates.find((t) => String(t.id) === tplSelect.value);
      const cfg = existing?.config ?? { column: Object.keys(columnValues)[0] ?? "", mapping: {}, tolerance_px: 1 };
      const nameIn = h("input", { value: existing?.name ?? "", placeholder: "template name" });
      const colSelect = h("select", { onChange: () => drawMapping() },
        ...Object.keys(columnValues).map((c) =>
          h("option", { value: c, selected: c === cfg.column }, `${c} (${columnValues[c].size} values)`)));
      const tolIn = h("input", { type: "number", min: 0, max: 100, step: 0.5, value: cfg.tolerance_px ?? 1, style: { width: "5em" } });
      const globIn = h("input", { value: cfg.file_glob ?? "", placeholder: "e.g. BB_*_labels.gpkg" });
      const extendSelect = h("select", {},
        h("option", { value: "" }, "(off)"),
        ...activeClasses.map((c) =>
          h("option", { value: c.id, selected: cfg.extend_class === c.id }, c.name)));
      const extendDistIn = h("input", { type: "number", min: 1, max: 500, value: cfg.extend_dist ?? 40, style: { width: "5em" } });
      const mappingBox = h("div", {});
      const valueSelects = new Map<string, HTMLSelectElement>();

      const drawMapping = () => {
        clear(mappingBox);
        valueSelects.clear();
        const vals = [...(columnValues[colSelect.value] ?? [])].sort();
        mappingBox.append(h("table", { className: "table" },
          h("thead", {}, h("tr", {}, h("th", {}, "Value in file"), h("th", {}, "Project class"))),
          h("tbody", {}, ...vals.map((v) => {
            const sel = h("select", {},
              h("option", { value: "" }, "(skip)"),
              ...activeClasses.map((c) =>
                h("option", { value: c.id, selected: cfg.column === colSelect.value && cfg.mapping[v] === c.id }, c.name)));
            valueSelects.set(v, sel);
            return h("tr", {}, h("td", {}, h("code", {}, v)), h("td", {}, sel));
          }))));
      };
      drawMapping();

      const collect = () => {
        const mapping: Record<string, number | null> = {};
        for (const [v, sel] of valueSelects) mapping[v] = sel.value ? Number(sel.value) : null;
        return { column: colSelect.value, mapping, tolerance_px: Number(tolIn.value), file_glob: globIn.value.trim(),
                 extend_class: extendSelect.value ? Number(extendSelect.value) : null,
                 extend_dist: Number(extendDistIn.value) || 40 };
      };

      editor.append(
        h("div", { className: "row gap wrap" },
          h("label", { className: "row gap" }, "Name", nameIn),
          h("label", { className: "row gap", title: "Only sets which column's values are listed below; at import time the class column is chosen per file" }, "Values from column", colSelect),
          h("label", { className: "row gap" }, "Snap tolerance (px)", tolIn),
          h("label", { className: "row gap", title: "Files matching this pattern preselect this template in the table above" }, "File pattern (default for matching files)", globIn),
          h("label", { className: "row gap", title: "After import, dangling line ends of this class are extended along their own direction until they hit another line (which is split into a junction), or connected to the closest line within the given distance" },
            "Auto-connect dangling ends of", extendSelect, "max px", extendDistIn)),
        mappingBox,
        h("div", { className: "row gap wrap" },
          h("button", { className: "btn btn-small", onClick: async () => {
              try {
                if (existing) {
                  await api(`/api/admin/ingest-templates/${existing.id}`, { method: "PUT", body: { name: nameIn.value, config: collect() } });
                } else {
                  await api(`/api/admin/projects/${p.id}/ingest-templates`, { method: "POST", body: { name: nameIn.value, config: collect() } });
                }
                toast("Template saved");
                void drawLabels();
              } catch (e: any) { toast(e.message, true); }
            } }, "Save template"),
          existing && h("button", { className: "btn btn-small btn-danger", onClick: async () => {
              if (await confirmDialog(`Delete template "${existing.name}"?`, "Delete")) {
                await api(`/api/admin/ingest-templates/${existing.id}`, { method: "DELETE" });
                void drawLabels();
              }
            } }, "Delete template"),
        ),
      );
    };
    tplSelect.addEventListener("change", drawEditor);
    drawEditor();

    box.append(
      h("h3", {}, "Ingestion templates"),
      h("div", { className: "row gap" }, h("label", { className: "row gap" }, "Template", tplSelect)),
      editor,
    );
    return box;
  }

  async function run(dryRun: boolean): Promise<void> {
    clear(body);
    body.append(h("p", { className: "muted" }, dryRun ? "Scanning…" : "Ingesting… this can take a while for many files."));
    try {
      const r = await api<any>(`/api/admin/projects/${project!.id}/ingest?dry_run=${dryRun}`, { method: "POST" });
      clear(body);
      drawIntro();
      if (!dryRun) void drawMasks(); // masked status of the listed tiles changed
      const counts: Record<string, number> = {};
      for (const f of r.files) counts[f.status] = (counts[f.status] ?? 0) + 1;
      const problems = (counts["error"] ?? 0) + (counts["unmatched"] ?? 0);
      add(
        body,
        h("h2", {}, dryRun ? "Scan result" : "Ingestion result"),
        r.files.length === 0
          ? h("p", { className: "muted" }, "No .tif files found in the input folder.")
          : h("details", { open: problems > 0 },
              h("summary", {},
                `${r.files.length} file(s): ` + Object.entries(counts).map(([s, n]) => `${n} ${s}`).join(", ")),
              h("table", { className: "table" },
                h("thead", {}, h("tr", {}, ...["File", "Tile", "Modality", "Status", "Message"].map((t) => h("th", {}, t)))),
                h("tbody", {}, ...r.files.map((f: any) =>
                  h("tr", { className: f.status === "error" || f.status === "unmatched" ? "row-bad" : "" },
                    h("td", {}, f.file), h("td", {}, f.basename || "–"), h("td", {}, f.modality || "–"),
                    h("td", {}, f.status), h("td", {}, f.message || "")))))),
        r.deletable_originals.length
          ? h("details", {},
              h("summary", {}, `Safe to delete from input/ (${r.deletable_originals.length} file(s))`),
              h("p", { className: "muted" }, "These originals are fully ingested (JPEG + geotransform stored):"),
              h("pre", { className: "codeblock" }, r.deletable_originals.join("\n")))
          : null,
      );
    } catch (e: any) {
      clear(body);
      drawIntro();
      body.append(h("p", { className: "form-error" }, e.message));
    }
  }
}

// ---------------- tiles ----------------

async function tilesTab(content: HTMLElement): Promise<void> {
  const body = h("div", {});
  const projects = await pickProject(content, (p) => void drawTiles(p));
  if (!projects) return;
  content.append(body);

  async function drawTiles(project: Project): Promise<void> {
    clear(body);
    const tiles = await api<any[]>(`/api/admin/projects/${project.id}/tiles`);
    if (tiles.length === 0) {
      body.append(h("p", { className: "muted pad" }, "No tiles ingested yet."));
      return;
    }
    const toReview = tiles.filter((t) => t.assignments.some((a: any) => a.status === "review"));
    let onlyReview = toReview.length > 0 && localStorage.getItem("labeling.tilesFilter") === "review";
    const filterBtn = h("button", { className: "btn btn-small" + (onlyReview ? " btn-primary" : ""),
      onClick: () => { localStorage.setItem("labeling.tilesFilter", onlyReview ? "all" : "review"); void drawTiles(project); } },
      `To review (${toReview.length})`);
    const shown = onlyReview ? toReview.slice().sort((a, b) => {
      const sa = Math.min(...a.assignments.filter((x: any) => x.status === "review").map((x: any) => x.submitted_at ?? 0));
      const sb = Math.min(...b.assignments.filter((x: any) => x.status === "review").map((x: any) => x.submitted_at ?? 0));
      return sa - sb;
    }) : tiles;
    body.append(
      h("div", { className: "row gap pad" },
        h("span", { className: "muted" }, `${tiles.length} tile(s) ingested`),
        toReview.length ? filterBtn : null,
        tiles.some((t) => t.seeded) ? h("button", { className: "btn btn-small", title: "Compute the diff-to-pre-labels summary for every seeded assignment whose cache is missing or stale (a few seconds per tile)",
          onClick: async (e: Event) => {
            const btn = e.currentTarget as HTMLButtonElement;
            btn.disabled = true; btn.textContent = "Computing diffs…";
            try {
              const r = await api<{ computed: number }>(`/api/admin/projects/${project.id}/compute-diffs`, { method: "POST" });
              toast(`${r.computed} diff summar${r.computed === 1 ? "y" : "ies"} computed`);
              void drawTiles(project);
            } catch (err: any) { toast(err.message, true); btn.disabled = false; btn.textContent = "Compute diffs"; }
          } }, "Compute diffs") : null,
        onlyReview ? h("span", { className: "muted small" }, "oldest submission first") : null),
      h("table", { className: "table" },
        h("thead", {}, h("tr", {}, ...["#", "Tile", "Modalities", "Queue", "Assignments", ""].map((t) => h("th", {}, t)))),
        h("tbody", {}, ...shown.map((t, i) => {
          const active = t.assignments.find((a: any) => a.status === "active" || a.status === "review");
          return h("tr", {},
            h("td", { className: "muted" }, String(i + 1)),
            h("td", {}, t.basename),
            h("td", { className: "muted" }, t.modalities.join(", ") + (t.seeded ? " · seeded" : "")),
            h("td", {}, t.queued ? "queued" : active ? "claimed" : "–"),
            h("td", {}, ...t.assignments.map((a: any) =>
              h("div", { className: "row gap assignment-row" },
                h("span", {}, `${a.username}: ${a.status} (${fmtDuration(a.seconds)})`),
                a.status === "review" ? h("span", { className: "review-badge", title: a.review_note ? `Last note: ${a.review_note}` : "" },
                    `round ${(a.review_rounds ?? 0) + 1} · submitted ${fmtDate(a.submitted_at)}`) : null,
                a.status === "active" && a.review_rounds ? h("span", { className: "muted", title: a.review_note }, `returned (round ${a.review_rounds})`) : null,
                a.diff_summary ? h("span", { className: "muted", title: `Diff to the pre-labels at ${a.diff_summary.tol} px: kept ${a.diff_summary.kept}, moved ${a.diff_summary.moved}, new ${a.diff_summary.new}, reclassed ${a.diff_summary.reclassed}, deleted ${a.diff_summary.deleted} px (baseline ${a.diff_summary.baseline_px} px). Open the tile and press Δ to see it.` },
                    `Δ ${a.diff_summary.changed_pct ?? "–"} % · +${fmtK(a.diff_summary.new)} −${fmtK(a.diff_summary.deleted)} ~${fmtK(a.diff_summary.moved)}`) : null,
                a.flags ? h("span", {
                    className: "flag-count" + (a.flags_waiting ? "" : a.flags_resolved === a.flags ? " flag-count-done" : " flag-count-quiet"),
                    title: `${a.flags_resolved} of ${a.flags} flag(s) resolved`
                      + (a.flags_waiting ? `, ${a.flags_waiting} waiting for you` : ", none waiting for you")
                      + " — open the tile (flag tool, N) to read, reply or resolve, or plant your own flag there",
                  }, `⚑ ${a.flags_resolved}/${a.flags}` + (a.flags_waiting ? ` · ${a.flags_waiting} for you` : "")) : null,
                a.retouched_by ? h("span", { className: "muted", title: "This finished tile was edited by an admin after being marked done" }, `retouched by ${a.retouched_by}`) : null,
                h("a", { className: "btn btn-small" + (a.status === "review" ? " btn-primary" : ""), href: `#/label/${a.id}` },
                  a.status === "done" ? "Inspect / retouche" : a.status === "review" ? "Review" : "Inspect"),
                h("a", { className: "btn btn-small", href: `/api/admin/assignments/${a.id}/gpkg` }, "GPKG"),
                a.status === "active" && h("button", { className: "btn btn-small btn-danger", onClick: async () => {
                    if (await confirmDialog(`Release ${t.basename} from ${a.username}? Their saved progress is kept.`, "Release")) {
                      await api(`/api/admin/assignments/${a.id}/release`, { method: "POST" });
                      void drawTiles(project);
                    }
                  } }, "Release"),
              ))),
            h("td", { className: "row gap wrap" },
              t.queued
                ? h("button", { className: "btn btn-small", onClick: async () => {
                    await api(`/api/admin/tiles/${t.id}/unqueue`, { method: "POST" });
                    void drawTiles(project);
                  } }, "Remove from queue")
                : !active && h("button", { className: "btn btn-small", onClick: async () => {
                    try {
                      await api(`/api/admin/tiles/${t.id}/requeue`, { method: "POST" });
                      void drawTiles(project);
                    } catch (e: any) { toast(e.message, true); }
                  } }, "Requeue"),
              h("button", { className: "btn btn-small btn-danger", onClick: () => deleteTileDialog(t, () => void drawTiles(project)) }, "Delete"),
            ),
          );
        })),
      ),
    );
  }
}

function deleteTileDialog(t: any, onDone: () => void): void {
  const doneCount = t.assignments.filter((a: any) => a.status === "done").length;
  const input = h("input", { placeholder: t.basename });
  const error = h("div", { className: "form-error" });
  const close = modal(
    h("form", { onSubmit: async (e: Event) => {
        e.preventDefault();
        try {
          const r = await api<any>(`/api/admin/tiles/${t.id}/delete`, { method: "POST", body: { confirm_basename: input.value } });
          close();
          toast(`Deleted: ${r.assignments_deleted} assignment(s), ${r.files_removed} file(s)`);
          onDone();
        } catch (err: any) { error.textContent = err.message; }
      } },
      h("h2", {}, `Delete tile ${t.basename}?`),
      h("p", {},
        "This removes the tile from the queue and deletes its converted images, ",
        `all ${t.assignments.length} assignment(s)`,
        doneCount ? ` — including ${doneCount} FINISHED label set(s) — ` : " ",
        "and all exported GPKG/raster files for this tile. This cannot be undone. ",
        "Type the tile name to confirm:"),
      input, error,
      h("div", { className: "row gap right" }, h("button", { className: "btn btn-danger", type: "submit" }, "Delete permanently")),
    ),
  );
}

// ---------------- export ----------------

async function exportTab(content: HTMLElement): Promise<void> {
  const body = h("div", { className: "pad" });
  const projects = await pickProject(content, (p) => void drawSpecs(p));
  if (!projects) return;
  content.append(body);

  async function drawSpecs(project: Project): Promise<void> {
    clear(body);
    const specs = await api<any[]>(`/api/admin/projects/${project.id}/export-specs`);
    const nameIn = h("input", { placeholder: "spec name (e.g. training-v2)" });
    body.append(
      h("p", { className: "muted" },
        "An export spec maps project classes to output classes (merge by giving several classes the same output name; ",
        "leave empty to drop a class) and re-exports labels to ",
        h("code", {}, `data/output/${project.name}/<spec>/`),
        " — as vector GPKG and/or multiband rasters (binary or distance-transform) on the reference grid. ",
        "Scope: all finished label sets, or hand-picked tiles (those export their best available labels — finished, else in-progress, else the imported seed)."),
      h("form", { className: "row gap", onSubmit: async (e: Event) => {
          e.preventDefault();
          try {
            const mappings: Record<string, { name: string; width: number }> = {};
            for (const c of project.classes.filter((c) => c.is_active)) mappings[String(c.id)] = { name: c.name, width: 3 };
            await api(`/api/admin/projects/${project.id}/export-specs`, {
              method: "POST",
              body: { name: nameIn.value, config: { mappings, raster: false } },
            });
            void drawSpecs(project);
          } catch (err: any) { toast(err.message, true); }
        } },
        nameIn, h("button", { className: "btn btn-primary btn-small", type: "submit" }, "New spec")),
      ...specs.map((spec) => specEditor(project, spec, () => void drawSpecs(project))),
    );
  }

  function specEditor(project: Project, spec: any, refresh: () => void): HTMLElement {
    const rawMappings: Record<string, any> = spec.config.mappings || {};
    const legacyWidth = spec.config.raster_line_width ?? 3;
    const entry = (cid: number): { name: string; width: number } => {
      const v = rawMappings[String(cid)];
      if (typeof v === "string") return { name: v, width: legacyWidth };
      return { name: v?.name ?? "", width: v?.width ?? legacyWidth };
    };
    const activeClasses = project.classes.filter((c) => c.is_active);
    const mapInputs = new Map<number, { name: HTMLInputElement; width: HTMLInputElement }>();
    const vectorsCheck = h("input", { type: "checkbox", checked: spec.config.vectors !== false });
    const rasterCheck = h("input", { type: "checkbox", checked: !!spec.config.raster });
    const distCheck = h("input", { type: "checkbox", checked: !!spec.config.distance });
    const distMaxIn = h("input", { type: "number", min: 1, max: 10000, step: 1, value: spec.config.distance_max ?? 64, style: { width: "5em" } });
    const clipCheck = h("input", { type: "checkbox", checked: !!spec.config.clip });
    const mergeCheck = h("input", { type: "checkbox", checked: !!spec.config.merge });
    const mergeDistIn = h("input", { type: "number", min: 0.01, max: 1000, step: 0.1, value: spec.config.merge_dist ?? 1, style: { width: "5em" } });
    const results = h("div", {});

    // --- export scope: all finished label sets, or hand-picked tiles ---
    const tileChecks = new Map<number, HTMLInputElement>();
    const selCount = h("span", { className: "muted" });
    const tileTableBox = h("div", {}, h("p", { className: "muted" }, "Loading…"));
    let tilesLoaded = false;
    const updateCount = (): void => {
      const n = [...tileChecks.values()].filter((cb) => cb.checked).length;
      selCount.textContent = `(${n}/${tileChecks.size} picked)`;
    };
    async function loadTiles(): Promise<void> {
      if (tilesLoaded) return;
      tilesLoaded = true;
      const tiles = await api<any[]>(`/api/admin/projects/${project.id}/export-tiles`);
      clear(tileTableBox);
      if (tiles.length === 0) {
        tileTableBox.append(h("p", { className: "muted" }, "No tiles ingested yet."));
        updateCount();
        return;
      }
      const allCb = h("input", { type: "checkbox", checked: true, title: "Select / deselect all tiles",
        onChange: () => {
          for (const cb of tileChecks.values()) if (!cb.disabled) cb.checked = allCb.checked;
          updateCount();
        } });
      tileTableBox.append(h("table", { className: "table" },
        h("thead", {}, h("tr", {}, h("th", {}, allCb),
          ...["#", "Tile", "Seeded", "Will export"].map((t) => h("th", {}, t)))),
        h("tbody", {}, ...tiles.map((t, i) => {
          const none = t.source === "none";
          const cb = h("input", { type: "checkbox", checked: !none, disabled: none, onChange: updateCount });
          if (!none) tileChecks.set(t.id, cb);
          const src = t.source === "done" ? (t.done_count > 1 ? `${t.done_count} finished label sets` : "finished label set")
            : t.source === "wip" ? "in-progress labels (marked _wip)"
            : t.source === "seed" ? "imported seed labels (annotator \"seed\")"
            : "nothing — tile has no labels at all";
          return h("tr", { className: none ? "row-bad" : "" },
            h("td", {}, cb),
            h("td", { className: "muted" }, String(i + 1)),
            h("td", {}, t.basename),
            h("td", {}, t.seeded ? "✓" : ""),
            h("td", { className: none ? "" : "muted" }, src));
        }))));
      updateCount();
    }
    const scopeAll = h("input", { type: "radio", name: `export-scope-${spec.id}`, checked: true });
    const scopeSel = h("input", { type: "radio", name: `export-scope-${spec.id}`,
      onChange: () => { if (scopeSel.checked) { tileDetails.open = true; } } });
    const tileDetails: HTMLDetailsElement = h("details", {
        onToggle: () => { if (tileDetails.open) { scopeSel.checked = true; void loadTiles(); } } },
      h("summary", {}, "Tile list ", selCount),
      tileTableBox);
    const scopeBox = h("div", {},
      h("div", { className: "row gap wrap" },
        h("label", { className: "row gap" }, scopeAll, "export all finished label sets"),
        h("label", { className: "row gap",
          title: "Per picked tile: every finished label set — or, if there is none, the newest unfinished claim (files marked _wip), or the imported seed labels (annotator \"seed\")" },
          scopeSel, "export picked tiles, best available labels each")),
      tileDetails);

    const collectConfig = () => {
      const m: Record<string, { name: string; width: number }> = {};
      for (const [cid, ins] of mapInputs) {
        m[String(cid)] = { name: ins.name.value.trim(), width: Number(ins.width.value) || 3 };
      }
      return { mappings: m, vectors: vectorsCheck.checked, raster: rasterCheck.checked,
        distance: distCheck.checked, distance_max: Number(distMaxIn.value) || 64,
        clip: clipCheck.checked, merge: mergeCheck.checked, merge_dist: Number(mergeDistIn.value) || 1 };
    };

    return h("section", { className: "spec-card" },
      h("h3", {}, spec.name),
      h("table", { className: "table" },
        h("thead", {}, h("tr", {},
          h("th", {}, "Project class"), h("th", {}, "Output class (empty = drop)"), h("th", {}, "Line width (px)"))),
        h("tbody", {}, ...activeClasses.map((c) => {
          const cur = entry(c.id);
          const name = h("input", { value: cur.name, placeholder: "(dropped)" });
          const width = h("input", { type: "number", min: 1, max: 100, value: cur.width, style: { width: "5em" } });
          mapInputs.set(c.id, { name, width });
          return h("tr", {},
            h("td", {}, h("span", { className: "classdot-small", style: { background: c.color } }), c.name),
            h("td", {}, name),
            h("td", {}, width));
        })),
      ),
      h("div", { className: "row gap wrap" },
        h("label", { className: "row gap", title: "Per-labelset GeoPackage with nodes, edges and flags; untick for raster-only exports" },
          vectorsCheck, "vector labels (GPKG)"),
        h("label", { className: "row gap", title: "Multiband GeoTIFF, one 0/255 band per output class, lines drawn at their class width" },
          rasterCheck, "binary rasters"),
        h("label", { className: "row gap", title: "Multiband float32 GeoTIFF (*_dist.tif), per band the euclidean distance in pixels to the nearest labeled pixel of that class, truncated at the max distance" },
          distCheck, "distance-transform rasters, max distance (px)", distMaxIn),
        h("label", { className: "row gap", title: "Crop exported rasters to the ground-truth mask's bounding box (tiles ingested with a *_mask.gpkg; others stay full size)" },
          clipCheck, "clip rasters to data area"),
        h("label", { className: "row gap", title: "One extra GPKG per CRS group: latest label set per tile, clamped to tile bounds, nodes merged across tiles within this distance" },
          mergeCheck, "merged per-CRS GPKG, node merge distance (world units)", mergeDistIn),
      ),
      scopeBox,
      h("div", { className: "row gap wrap" },
        h("span", { className: "spacer" }),
        h("button", { className: "btn btn-small", onClick: async () => {
            try {
              await api(`/api/admin/export-specs/${spec.id}`, { method: "PUT", body: { name: spec.name, config: collectConfig() } });
              toast("Spec saved");
            } catch (e: any) { toast(e.message, true); }
          } }, "Save"),
        h("button", { className: "btn btn-primary btn-small", onClick: async () => {
            try {
              const runBody: { tile_ids?: number[] } = {};
              if (scopeSel.checked) {
                await loadTiles();
                runBody.tile_ids = [...tileChecks].filter(([, cb]) => cb.checked).map(([id]) => id);
                if (runBody.tile_ids.length === 0) { toast("No tiles picked", true); return; }
              }
              await api(`/api/admin/export-specs/${spec.id}`, { method: "PUT", body: { name: spec.name, config: collectConfig() } });
              clear(results);
              results.append(h("p", { className: "muted" }, "Exporting…"));
              const r = await api<any>(`/api/admin/export-specs/${spec.id}/run`, { method: "POST", body: runBody });
              clear(results);
              const errs = r.exported.filter((x: any) => x.status !== "ok");
              add(
                results,
                h("p", {}, `Exported ${r.count - errs.length}/${r.count} label sets (${r.exported.reduce((n: number, x: any) => n + x.files.length, 0)} files). Class mapping written to `, h("code", {}, r.classes_md), "."),
                (r.skipped ?? []).length > 0 && h("p", { className: "muted" }, `Skipped — no labels at all: ${r.skipped.join(", ")}`),
                (r.merged ?? []).length > 0 && h("p", {}, "Merged per-CRS: ", h("code", {}, r.merged.join(", "))),
                errs.length > 0 && h("pre", { className: "codeblock" }, errs.map((x: any) => `${x.tile ?? "assignment " + x.assignment_id}: ${x.status}`).join("\n")),
              );
            } catch (e: any) { clear(results); toast(e.message, true); }
          } }, "Save + run export"),
        h("button", { className: "btn btn-small btn-danger", onClick: async () => {
            if (await confirmDialog(`Delete export spec "${spec.name}"? Already exported files stay on disk.`, "Delete")) {
              await api(`/api/admin/export-specs/${spec.id}`, { method: "DELETE" });
              refresh();
            }
          } }, "Delete"),
      ),
      results,
    );
  }
}

// ---------------- time ----------------

async function timeTab(content: HTMLElement): Promise<void> {
  const r = await api<any>("/api/admin/time");
  clear(content);
  content.append(
    h("section", { className: "pad" },
      h("h2", {}, "Per user"),
      h("table", { className: "table" },
        h("thead", {}, h("tr", {}, ...["User", "Total time", "Tiles done"].map((t) => h("th", {}, t)))),
        h("tbody", {}, ...r.per_user.map((u: any) =>
          h("tr", { className: u.is_active ? "" : "row-inactive" },
            h("td", {}, u.username), h("td", {}, fmtDuration(u.total_seconds)), h("td", {}, String(u.tiles_done ?? 0))))))),
    h("section", { className: "pad" },
      h("h2", {}, "Per assignment"),
      h("table", { className: "table" },
        h("thead", {}, h("tr", {}, ...["Project", "Tile", "User", "Status", "Time", "Claimed", "Done"].map((t) => h("th", {}, t)))),
        h("tbody", {}, ...r.assignments.map((a: any) =>
          h("tr", {},
            h("td", {}, a.project), h("td", {}, a.basename), h("td", {}, a.username),
            h("td", {}, a.status), h("td", {}, fmtDuration(a.seconds)),
            h("td", {}, fmtDate(a.claimed_at)), h("td", {}, fmtDate(a.done_at))))))),
  );
}

// ---------------- pay ----------------

const PAY_USER_KEY = "labeling.payUser";

/** "1.5" or "1:30" → seconds; NaN when unparseable. */
function parseHours(s: string): number {
  s = s.trim().replace(",", ".");
  const m = s.match(/^(\d+):(\d{1,2})$/);
  if (m) return Number(m[1]) * 3600 + Number(m[2]) * 60;
  const hrs = Number(s);
  return Number.isFinite(hrs) && s !== "" ? Math.round(hrs * 3600) : NaN;
}

/** "17.50" / "17,50" → cents; NaN when unparseable. */
function parseEuro(s: string): number {
  s = s.trim().replace(",", ".");
  const v = Number(s);
  return Number.isFinite(v) && s !== "" ? Math.round(v * 100) : NaN;
}

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Void dialog: a reason is optional but the question is explicit about what voiding means. */
function voidDialog(what: string, onConfirm: (note: string) => Promise<void>): void {
  const note = h("input", { placeholder: "reason (optional)" });
  const error = h("div", { className: "form-error" });
  const close = modal(
    h("form", { onSubmit: async (e: Event) => {
        e.preventDefault();
        try { await onConfirm(note.value); close(); } catch (err: any) { error.textContent = err.message; }
      } },
      h("h2", {}, "Void entry?"),
      h("p", {}, what),
      h("p", { className: "muted" }, "The entry stays in the database marked as voided and no longer counts."),
      note, error,
      h("div", { className: "row gap right" },
        h("button", { className: "btn", type: "button", onClick: () => close() }, "Cancel"),
        h("button", { className: "btn btn-danger", type: "submit" }, "Void"))),
  );
  note.focus();
}

async function payTab(content: HTMLElement): Promise<void> {
  const overview = await api<any[]>("/api/admin/pay/overview");
  clear(content);
  const saved = Number(localStorage.getItem(PAY_USER_KEY));
  let selected = overview.find((u) => u.user_id === saved)?.user_id ?? overview[0]?.user_id ?? null;
  const detail = h("div", {});

  const table = h("table", { className: "table" },
    h("thead", {}, h("tr", {}, ...["User", "Rate", "This month", "Earned", "Paid", "Balance", ""].map((t) => h("th", {}, t)))),
    h("tbody", {}, ...overview.map((u) =>
      h("tr", { className: (u.is_active ? "" : "row-inactive") + (u.user_id === selected ? " row-selected" : "") },
        h("td", {}, u.username, u.is_admin ? h("span", { className: "muted" }, " (admin)") : null),
        h("td", {}, fmtRate(u.current_rate)),
        h("td", {}, `${fmtHours(u.month_seconds)} · ${fmtEuro(u.month_earned_cents)}`),
        h("td", {}, fmtEuro(u.earned_cents + u.adjustment_cents),
          u.unpriced_seconds > 0 ? h("span", { className: "form-error", title: "time on days without a rate" }, ` ⚠ ${fmtHours(u.unpriced_seconds)} unpriced`) : null),
        h("td", {}, fmtEuro(u.payment_cents)),
        h("td", { className: u.balance_cents < 0 ? "pay-negative" : "" }, fmtEuro(u.balance_cents)),
        h("td", {}, h("button", { className: "btn btn-small", onClick: () => {
            selected = u.user_id; localStorage.setItem(PAY_USER_KEY, String(u.user_id));
            void payTab(content);
          } }, "Open")),
      ))),
    h("tfoot", {}, (() => {
      const sum = (k: string) => overview.reduce((n, u) => n + (u[k] as number), 0);
      const unpriced = sum("unpriced_seconds");
      return h("tr", { className: "pay-total" },
        h("td", {}, "Total"),
        h("td", {}, ""),
        h("td", {}, `${fmtHours(sum("month_seconds"))} · ${fmtEuro(sum("month_earned_cents"))}`),
        h("td", {}, fmtEuro(sum("earned_cents") + sum("adjustment_cents")),
          unpriced > 0 ? h("span", { className: "form-error", title: "time on days without a rate" }, ` ⚠ ${fmtHours(unpriced)} unpriced`) : null),
        h("td", {}, fmtEuro(sum("payment_cents"))),
        h("td", { className: sum("balance_cents") < 0 ? "pay-negative" : "" }, fmtEuro(sum("balance_cents"))),
        h("td", {}, ""));
    })()));
  content.append(
    h("section", { className: "pad" },
      h("h2", {}, "Overview"),
      h("p", { className: "muted small" }, "Balance = everything earned (incl. adjustments) minus everything paid, over all time. Time is booked per calendar day in German time."),
      table),
    detail,
  );
  if (selected !== null) await payDetail(detail, selected, () => void payTab(content));
}

async function payDetail(el: HTMLElement, userId: number, refresh: () => void): Promise<void> {
  const st = await api<Statement>(`/api/admin/users/${userId}/statement`);
  clear(el);
  const post = async (path: string, body: any, okMsg: string) => {
    try {
      await api(`/api/admin/users/${userId}/${path}`, { method: "POST", body });
      toast(okMsg);
      refresh();
    } catch (e: any) { toast(e.message, true); }
  };

  // ---- entry forms
  const rateDay = h("input", { type: "date", value: today() });
  const rateVal = h("input", { placeholder: "€ per hour", inputMode: "decimal", style: { width: "110px" } });
  const rateNote = h("input", { placeholder: "note (optional)" });
  const rateForm = form("New rate", "Applies from that day on; the previous rate stays for earlier days.", [rateDay, rateVal, rateNote], "Add rate", () => {
    const cents = parseEuro(rateVal.value);
    if (Number.isNaN(cents) || cents < 0) { toast("Enter a rate like 17.50", true); return; }
    void post("rates", { valid_from: rateDay.value, cents_per_hour: cents, note: rateNote.value }, "Rate added");
  });

  const payDay = h("input", { type: "date", value: today() });
  const payVal = h("input", { placeholder: "€ paid", inputMode: "decimal", style: { width: "110px" } });
  const payNote = h("input", { placeholder: "reference, e.g. bank transfer" });
  const payForm = form("Record payment", "Money handed over to the labeler.", [payDay, payVal, payNote], "Record", () => {
    const cents = parseEuro(payVal.value);
    if (Number.isNaN(cents) || cents <= 0) { toast("Enter a positive amount like 50.00", true); return; }
    void post("ledger", { kind: "payment", day: payDay.value, amount_cents: cents, note: payNote.value }, "Payment recorded");
  });

  const adjDay = h("input", { type: "date", value: today() });
  const adjVal = h("input", { placeholder: "± €", inputMode: "decimal", style: { width: "110px" } });
  const adjNote = h("input", { placeholder: "reason (required)" });
  const adjForm = form("Adjustment", "Signed amount added to what is owed: bonus, correction, expense.", [adjDay, adjVal, adjNote], "Add", () => {
    const cents = parseEuro(adjVal.value);
    if (Number.isNaN(cents) || cents === 0) { toast("Enter a non-zero amount like -12.50", true); return; }
    void post("ledger", { kind: "adjustment", day: adjDay.value, amount_cents: cents, note: adjNote.value }, "Adjustment added");
  });

  const workDay = h("input", { type: "date", value: today() });
  const workVal = h("input", { placeholder: "hours, e.g. 1.5 or 1:30", style: { width: "150px" } });
  const workNote = h("input", { placeholder: "what for (required)" });
  const workForm = form("Manual time", "Paid time outside the editor: meetings, onboarding, corrections.", [workDay, workVal, workNote], "Add time", () => {
    const secs = parseHours(workVal.value);
    if (Number.isNaN(secs) || secs <= 0) { toast("Enter hours like 1.5 or 1:30", true); return; }
    void post("work", { day: workDay.value, seconds: secs, note: workNote.value }, "Time added");
  });

  const ratesTable = st.rates.length
    ? h("table", { className: "table pay-table" },
        h("thead", {}, h("tr", {}, ...["Valid from", "Rate", "Note", "Entered by", ""].map((t) => h("th", {}, t)))),
        h("tbody", {}, ...st.rates.map((r) =>
          h("tr", {},
            h("td", {}, r.valid_from), h("td", {}, fmtRate(r.cents_per_hour)),
            h("td", {}, r.note || h("span", { className: "muted" }, "–")),
            h("td", { className: "muted" }, `${r.created_by_name}, ${fmtDate(r.created_at)}`),
            h("td", {}, h("button", { className: "btn btn-small btn-link", onClick: async () => {
                if (await confirmDialog(`Remove the rate of ${fmtRate(r.cents_per_hour)} from ${r.valid_from}? Days it covered fall back to the previous rate, or become unpriced.`, "Remove")) {
                  try {
                    await api(`/api/admin/users/${userId}/rates/${r.id}`, { method: "DELETE" });
                    refresh();
                  } catch (e: any) { toast(e.message, true); }
                }
              } }, "remove"))))))
    : h("p", { className: "muted" }, "No rate yet — time is logged but not priced.");

  el.append(
    h("section", { className: "pad" },
      h("h2", {}, `${st.username}`),
      h("div", { className: "pay-forms" }, rateForm, payForm, adjForm, workForm),
      h("h3", {}, "Rates"), ratesTable,
      h("h3", {}, "Statement"),
      renderStatement(st, false, {
        voidWork: (e: WorkEntry, day: string) => voidDialog(
          `${fmtDuration(e.seconds)} on ${day}: ${e.source === "manual" ? e.note : e.tile}`,
          async (note) => { await api(`/api/admin/users/${userId}/work/${e.id}/void`, { method: "POST", body: { note } }); refresh(); }),
        voidLedger: (l: LedgerEntry) => voidDialog(
          `${l.kind === "payment" ? "Payment" : "Adjustment"} of ${fmtEuro(l.amount_cents)} on ${l.day}${l.note ? ` (${l.note})` : ""}`,
          async (note) => { await api(`/api/admin/users/${userId}/ledger/${l.id}/void`, { method: "POST", body: { note } }); refresh(); }),
      })),
  );

  function form(title: string, hint: string, inputs: HTMLElement[], label: string, submit: () => void): HTMLElement {
    return h("form", { className: "pay-form", onSubmit: (e: Event) => { e.preventDefault(); submit(); } },
      h("h4", {}, title),
      h("div", { className: "row gap wrap" }, ...inputs, h("button", { className: "btn btn-primary btn-small", type: "submit" }, label)),
      h("div", { className: "muted small" }, hint));
  }
}
